import React, { useMemo } from 'react';
import { Checkbox, DefaultButton, Icon, DefaultPalette } from '@fluentui/react';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { DetailsListView } from '../Common/DetailsList/DetailListView';
import { GRID_MODE, OPERATORS, TYPES } from '../../common/list-utils';
import { ListColumnFilter } from '../Common/list-entities/ListColumnFilter';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TimeTaxSupportSystemClient from '../../system';
import MessageCourier from '../../lib/MessageCourier';
import { User } from '../../system/User';
import { routes, newEntityDefaultId } from '../../common/constants';
import DialogConfirmDelete from './DialogConfirmDelete';
import ProyectosGlosaDetailDialog from './ProyectosGlosaDetailDialog';
import ProyectosGlosaEditDialog from './ProyectosGlosaEditDialog';
import { defaultCommandBarButtonStyles } from '../../styles/button-styles';
import ViewWrapperWithCommandBar from '../Layout/ViewWrapperWithCommandBar';
import { ProyectoGlosa } from '../../system/proyectos_glosa/ProyectosGlosaSystem';
import { DateCustom } from '../../lib/DateCustom';

interface ProyectosGlosaListProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  user: User;
  changeBlockUI: (state: boolean) => void;
}

const MAX_SELECTED = 100;
const KEY_LIST = 'proyectos-glosa';

export const ProyectosGlosaList: React.FC<ProyectosGlosaListProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
  const [currentPageItems, setCurrentPageItems] = React.useState<ProyectoGlosa[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState<boolean>(false);
  const [refreshPage, setRefreshPage] = React.useState<boolean>(false);
  const [selectedDetailItem, setSelectedDetailItem] = React.useState<ProyectoGlosa | null>(null);
  const [showDetailDialog, setShowDetailDialog] = React.useState<boolean>(false);
  const [showEditDialog, setShowEditDialog] = React.useState<boolean>(false);
  const [editItemId, setEditItemId] = React.useState<string>('');

  const baseUrl = React.useMemo(() => {
    return props.system.getProyectosGlosaSystem().getBaseUrlForDatagrid();
  }, []);

  const quickFilters: ListColumnFilter[] = useMemo(() => {
    return [
      {
        quick: true,
        name: 'dicprog_nombre_proyecto',
        type: TYPES.INPUT,
        placeholder: t('Nombre del proyecto'),
        operator: OPERATORS.CONTAINS,
      },
      {
        quick: true,
        name: 'dicprog_activo',
        type: TYPES.CHECKBOX_LIST,
        labelForCheckbox: t('Activos'),
        checkBoxValue: '1',
        operator: OPERATORS.EQUAL,
        defaultValue: '1',
      },
      {
        quick: true,
        name: 'dicprog_fecha_validez_inicio',
        type: TYPES.DATE,
        placeholder: t('Inicio de vigencia'),
        operator: OPERATORS.AFTERORON,
        formatDate: DateCustom.formatDate,
      },
      {
        quick: true,
        name: 'dicprog_fecha_validez_fin',
        type: TYPES.DATE,
        placeholder: t('Fin de vigencia'),
        operator: OPERATORS.BEFOREORON,
        formatDate: DateCustom.formatDate,
      },
    ];
  }, []);

  const handleSelectItem = (item: ProyectoGlosa, checked?: boolean) => {
    if (checked) {
      if (selectedItems.length >= MAX_SELECTED) {
        props.courier.messageError(t('Se alcanzó el límite máximo de 100 filas seleccionadas'));
        return;
      }
      setSelectedItems((prev) => [...prev, item.dicprog_id]);
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== item.dicprog_id));
    }
  };

  const handleSelectAll = (checked?: boolean) => {
    const currentPageIds = currentPageItems.map((item: ProyectoGlosa) => item.dicprog_id);
    if (checked) {
      const merged = Array.from(new Set([...selectedItems, ...currentPageIds]));
      if (merged.length > MAX_SELECTED) {
        props.courier.messageError(t('Se alcanzó el límite máximo de 100 filas seleccionadas'));
      }
      setSelectedItems(merged.slice(0, MAX_SELECTED));
    } else {
      setSelectedItems((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    }
  };

  const handleSetItems: React.Dispatch<React.SetStateAction<ProyectoGlosa[]>> = (value) => {
    const items = value instanceof Function ? value(currentPageItems) : value;
    setCurrentPageItems(items);
  };

  const handleConfirmDelete = async () => {
    setShowConfirmDialog(false);
    props.changeBlockUI(true);
    try {
      const result = await props.system.getProyectosGlosaSystem().deleteMassive(selectedItems);
      if (result.errors.length > 0) {
        return props.courier.messageError(`${result.errors.join('. ')}`);
      }
      props.courier.messageSuccess(t(`Se eliminaron {{count}} registro(s)`, { count: result.deleted }));
    } catch (error) {
      console.log(error);
      props.courier.messageError(t('Error al eliminar los registros'));
    }
    setRefreshPage((prev) => !prev);
    setCurrentPageItems([]);
    setSelectedItems([]);
    props.changeBlockUI(false);
  };

  const handleEditFromDetail = (item: ProyectoGlosa) => {
    setShowDetailDialog(false);
    setEditItemId(item.dicprog_id);
    setShowEditDialog(true);
  };

  const handleEditDialogClosed = () => {
    setShowEditDialog(false);
    setEditItemId('');
  };

  const handleEditDialogSaved = () => {
    setShowEditDialog(false);
    setEditItemId('');
    setSelectedDetailItem(null);
    setRefreshPage((prev) => !prev);
    setCurrentPageItems([]);
  };

  const handleDeleteFromDetail = async (item: ProyectoGlosa) => {
    setShowDetailDialog(false);
    props.changeBlockUI(true);
    try {
      await props.system.getProyectosGlosaSystem().delete(item.dicprog_id);
      props.courier.messageSuccess(t('Registro eliminado exitosamente'));
    } catch (error) {
      console.log(error);
      props.courier.messageError(t('Error al eliminar el registro'));
    }
    setRefreshPage((prev) => !prev);
    setCurrentPageItems([]);
    setSelectedDetailItem(null);
    setSelectedItems((prev) => prev.filter((p) => p !== item.dicprog_id));
    props.changeBlockUI(false);
  };

  const handleCloseDetail = () => {
    setShowDetailDialog(false);
    setSelectedDetailItem(null);
  };

  const allCurrentPageItemsSelected =
    currentPageItems.length > 0 && currentPageItems.every((item: ProyectoGlosa) => selectedItems.includes(item.dicprog_id));

  const someCurrentPageItemsSelected =
    currentPageItems.some((item: ProyectoGlosa) => selectedItems.includes(item.dicprog_id)) && !allCurrentPageItemsSelected;

  const columns: any[] = [
    {
      fieldName: 'seleccionar',
      name: '',
      _onRender: (item: ProyectoGlosa) => (
        <div onClick={(event) => event.stopPropagation()}>
          <Checkbox
            checked={selectedItems.includes(item.dicprog_id)}
            onChange={(event, checked) => {
              event?.stopPropagation();
              handleSelectItem(item, checked);
            }}
            styles={{
              checkbox: {
                borderColor: '#c6c6c6',
                cursor: 'pointer',
              },
            }}
          />
        </div>
      ),
      onRenderHeader: () => (
        <div onClick={(event) => event.stopPropagation()} style={{ marginTop: 15 }}>
          <Checkbox
            checked={allCurrentPageItemsSelected}
            indeterminate={someCurrentPageItemsSelected}
            onChange={(event, checked) => {
              event?.stopPropagation();
              handleSelectAll(checked);
            }}
            styles={{
              checkbox: {
                borderColor: '#c6c6c6',
              },
            }}
          />
        </div>
      ),
      maxWidth: 32,
      minWidth: 32,
      isResizable: false,
      isPadded: false,
      onColumnClick: () => {},
    },
    {
      fieldName: 'dicprog_nombre_proyecto',
      name: t('Nombre del proyecto'),
      minWidth: 200,
    },
    {
      fieldName: 'dicprog_fecha_validez_inicio',
      name: t('Inicio de vigencia'),
      maxWidth: 120,
      minWidth: 120,
    },
    {
      fieldName: 'dicprog_fecha_validez_fin',
      name: t('Fin de vigencia'),
      maxWidth: 120,
      minWidth: 120,
    },
    {
      fieldName: 'dicprog_activo',
      name: t('Activo'),
      _onRender: (item: ProyectoGlosa) =>
        item.dicprog_activo ? <Icon iconName="CheckMark" className="color-success" /> : <Icon iconName="Cancel" className="color-error" />,
      maxWidth: 60,
      minWidth: 60,
    },
  ];

  const itemsMenu = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Proyectos comerciales'),
        iconProps: {
          iconName: 'Dictionary',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: defaultCommandBarButtonStyles,
        className: 'text-title-btn',
      },
      {
        key: 'new',
        text: t('Nuevo'),
        iconProps: {
          iconName: 'Add',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: defaultCommandBarButtonStyles,
        className: 'text-title-btn',
        onClick: () => navigate(`${routes.proyectos_glosa}/${newEntityDefaultId}`),
      },
    ],
    [],
  );

  const bulkActionBar =
    selectedItems.length > 0 ? (
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: '8px 0' }}>
        <DefaultButton
          iconProps={{ iconName: 'Delete' }}
          text={t('Eliminar seleccionados')}
          styles={{
            root: { borderColor: '#d13438', color: '#d13438' },
            rootHovered: { borderColor: '#a4262c', color: '#a4262c', backgroundColor: '#fde7e9' },
            icon: { color: '#d13438' },
          }}
          onClick={() => setShowConfirmDialog(true)}
        />
        <span style={{ fontSize: 13, color: '#605e5c' }}>
          {selectedItems.length} {selectedItems.length === 1 ? t('fila seleccionada') : t('filas seleccionadas')}
        </span>
      </div>
    ) : undefined;

  return (
    <ViewWrapperWithCommandBar withBorderBottom title="" items={itemsMenu} overflowButtonStyles={defaultCommandBarButtonStyles} variant="standard">
      <>
        <DialogConfirmDelete
          isOpen={showConfirmDialog}
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowConfirmDialog(false)}
          title={t('Eliminación masiva')}
          message={t('¿Deseas eliminar los registros seleccionados? Digita la palabra DELETE.')}
          confirmWord="DELETE"
          confirmText={t('Continuar')}
          cancelText={t('Cancelar')}
        />
        <ProyectosGlosaDetailDialog
          show={showDetailDialog}
          item={selectedDetailItem}
          onEdit={handleEditFromDetail}
          onDelete={handleDeleteFromDetail}
          onClose={handleCloseDetail}
        />
        <ProyectosGlosaEditDialog
          show={showEditDialog}
          itemId={editItemId}
          system={props.system}
          courier={props.courier}
          changeBlockUI={props.changeBlockUI}
          onClose={handleEditDialogClosed}
          onSaved={handleEditDialogSaved}
        />
        <DetailsListView
          refresh={refreshPage}
          keyList={KEY_LIST}
          onClickCardMode={() => {}}
          onClickGridMode={() => {}}
          mode={GRID_MODE}
          title={t('Proyectos comerciales')}
          onItemClick={(item: ProyectoGlosa) => {
            setSelectedDetailItem(item);
            setShowDetailDialog(true);
          }}
          cols={columns}
          isGridList={true}
          listFilters={[]}
          quickFilters={quickFilters}
          baseUrl={baseUrl}
          changeBlockUI={props.changeBlockUI}
          courier={props.courier}
          setItems={handleSetItems}
          renderBeforeHeader={bulkActionBar}
        />
      </>
      <br />
      <br />
    </ViewWrapperWithCommandBar>
  );
};

export default ProyectosGlosaList;
